import React, { useState, useEffect } from "react";
import { Upload, Button, message, Spin } from "antd";
import { LoadingOutlined, CameraOutlined } from "@ant-design/icons";
import imageCompression from "browser-image-compression";

const AntdImageUploader = ({
    watermarkImg,
    compndata,
    finalizeCanvas,
    rotationDegrees,
    setProcessing,
    initialImage, // ✅ new prop for edit mode (existing image)
}) => {
    const [fileInfo, setFileInfo] = useState(initialImage || null);
    const [uploading, setUploading] = useState(false);
    const [fileName, setFileName] = useState("");
    const [hasUserUploaded, setHasUserUploaded] = useState(false);
    // ✅ When initialImage changes (e.g. on edit open), sync it
    useEffect(() => {
        if (!hasUserUploaded && initialImage) {
            setFileInfo(initialImage);
        }
    }, [initialImage, hasUserUploaded]);

    const beforeUpload = (file) => {
        const allowedExtensions = ["jpg", "jpeg"];
        const ext = file.name.split(".").pop().toLowerCase();
        if (!allowedExtensions.includes(ext)) {
            message.error("Please use JPG or JPEG format.");
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const handleChange = async (info) => {
        const fileList = info.fileList;
        if (!fileList || fileList.length === 0) {
            setFileInfo(null);
            setFileName("");
            setHasUserUploaded(false);
            return;
        }
        setHasUserUploaded(true)
        setUploading(true);
        const lastFile = fileList[fileList.length - 1].originFileObj;

        try {
            setProcessing(true);
            // --- Step 1: Quick preview ---
            const previewOptions = { maxSizeMB: 0.05, maxWidthOrHeight: 200, useWebWorker: true };
            const previewFile = await imageCompression(lastFile, previewOptions);
            const previewUrl = URL.createObjectURL(previewFile);
            setFileInfo(previewUrl);
            setFileName(lastFile.name);

            // --- Step 2: Upload original asynchronously ---
            (async () => {
                try {
                    const formData = new FormData();
                    formData.append("file", lastFile);
                    await fetch("/api/upload", { method: "POST", body: formData });
                    console.log("Original uploaded in background.");
                } catch (uploadError) {
                    setUploading(false)
                    console.error("Upload failed:", uploadError);
                }
            })();

            // --- Step 3: Full processing ---
            setTimeout(async () => {
                try {
                    const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
                    const compressedFile = await imageCompression(lastFile, options);
                    const imageDataUrl = await imageCompression.getDataUrlFromFile(compressedFile);
                    const img = new Image();
                    img.src = imageDataUrl;
                    await new Promise((resolve) => (img.onload = resolve));

                    const watermarkEnabled = compndata[0]?.attributes?.WMark === true;
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    const maxWidth = 1920;
                    const scale = Math.min(1, maxWidth / img.width);
                    canvas.width = img.width * scale;
                    canvas.height = img.height * scale;
                    // Fill background to avoid black JPEG background
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    // Then draw image
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    // ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    if (watermarkEnabled && watermarkImg) {
                        const watermarkWidth = canvas.width * 0.3;
                        const watermarkHeight = (watermarkImg.height / watermarkImg.width) * watermarkWidth;
                        const watermarkX = (canvas.width - watermarkWidth) / 2;
                        const watermarkY = (canvas.height - watermarkHeight) / 2;
                        ctx.globalAlpha = 0.3;
                        ctx.drawImage(watermarkImg, watermarkX, watermarkY, watermarkWidth, watermarkHeight);
                    }

                    const compressedCanvasBlob = await new Promise((resolve) =>
                        canvas.toBlob(resolve, "image/jpeg", 0.8)
                    );

                    const finalPreviewUrl = URL.createObjectURL(compressedCanvasBlob);
                    setFileInfo(finalPreviewUrl);
                    finalizeCanvas(canvas, "image/jpeg");
                } catch (error) {
                    console.error("Processing failed:", error);
                } finally {
                    // setProcessing(false);
                    setTimeout(() => setProcessing(false), 1000);
                }
            }, 0);
        } catch (error) {
            console.error("Preview error:", error);
            message.error("Failed to process image preview.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Upload
            accept=".jpg,.jpeg"
            showUploadList={false}
            beforeUpload={beforeUpload}
            customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)}
            onChange={handleChange}
        >
            <Button
                style={{
                    width: 255,
                    height: 255,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {uploading ? (
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                ) : fileInfo ? (
                    <img
                        src={fileInfo}
                        style={{
                            transform: `rotate(${rotationDegrees}deg)`,
                            objectFit: "contain",
                            maxWidth: "100%",
                            maxHeight: "100%",
                            display: "block",
                        }}
                        width="100%"
                        height="100%"
                    />
                ) : (
                    <CameraOutlined style={{ fontSize: 80 }} />
                )}
            </Button>
        </Upload>
    );
};

export default AntdImageUploader;
